import { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DefaultCard from '../../components/default/DefaultCard';
import PageHeader from '../../components/PageHeader/PageHeader';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import LazyLoading from '../../components/LazyLoading';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import useChecklistService from '../../app/services/hooks/useChecklistService';
import { AppContext } from '../../contexts/AppContext';
import '../../styles/page-components.css';

interface Option {
    id: number;
    option_text: string;
    score: number;
}

interface Question {
    id: number;
    question_text: string;
    help_text: string;
    is_required: boolean;
    allow_multiple: boolean;
    options: Option[];
}

interface Checklist {
    id: number;
    name: string;
    description: string;
    instructions: string;
    questions: Question[];
    total_questions: number;
    max_possible_score: number;
}

const ChecklistRespond = () => {
    const history = useHistory();
    const { id } = useParams<{ id: string }>();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { 
        startChecklist, 
        submitChecklist,
        fetchingStartChecklist,
        fetchingSubmitChecklist
    } = useChecklistService();
    
    const [checklist, setChecklist] = useState<Checklist | null>(null);
    const [answers, setAnswers] = useState<Record<number, number[]>>({});
    const [currentStep, setCurrentStep] = useState<'intro' | 'questions' | 'review'>('intro');
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

    const breadcrumbs = [
        { label: 'Inicio', path: '/intranet' },
        { label: 'Checklists', path: '/intranet/checklists' },
        { label: checklist?.name || 'Responder', isCurrent: true }
    ];

    useEffect(() => {
        if (id) {
            loadChecklist(parseInt(id));
        }
    }, [id]);

    const loadChecklist = (checklistId: number) => {
        startChecklist(checklistId, {
            onSuccess: (response: ServiceResponse) => {
                setChecklist(response.data.checklist);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                history.push('/intranet/checklists');
            }
        });
    };

    const handleSelectOption = (questionId: number, optionId: number, allowMultiple: boolean) => {
        if (allowMultiple) {
            const current = answers[questionId] || [];
            const isSelected = current.includes(optionId);
            setAnswers({
                ...answers,
                [questionId]: isSelected
                    ? current.filter(id => id !== optionId)
                    : [...current, optionId]
            });
        } else {
            setAnswers({ ...answers, [questionId]: [optionId] });
        }
    };

    const handleNext = () => {
        if (!checklist) return;
        
        // Las preguntas se pueden dejar sin marcar: la instrucción del checklist
        // indica que si no se tiene lo consultado, se deja vacío.
        if (currentQuestionIndex < checklist.questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            setCurrentStep('review');
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleSubmit = () => {
        if (!checklist) return;

        const data = {
            checklist_id: checklist.id,
            answers: Object.entries(answers)
                .filter(([_, optionIds]) => optionIds.length > 0)
                .map(([questionId, optionIds]) => ({
                    question_id: parseInt(questionId),
                    option_ids: optionIds
                }))
        };

        // Se pueden dejar preguntas en blanco, pero el informe necesita al menos una respuesta
        if (data.answers.length === 0) {
            toast.warning('Marca al menos una opción para generar el informe.');
            return;
        }

        if (showLoading) showLoading('loading', 'Procesando respuestas y generando informe...');

        submitChecklist(data, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.success('¡Checklist completado!');
                history.push(`/intranet/checklists/result/${response.data.response.id}`);
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
            },
            onFieldError: () => {
                if (hideLoading) hideLoading();
                toast.error('Revisa tus respuestas e inténtalo nuevamente.');
            }
        });
    };

    const getAnsweredCount = () => {
        return Object.entries(answers).filter(([_, opts]) => opts.length > 0).length;
    };

    const getProgress = () => {
        if (!checklist) return 0;
        return Math.round((getAnsweredCount() / checklist.total_questions) * 100);
    };

    if (fetchingStartChecklist || !checklist) {
        return (
            <div className="container-fluid">
                <div className="page-container">
                    <LazyLoading height={400} />
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="page-container">
                <PageHeader title="" breadcrumbs={breadcrumbs} />

                {/* Barra de progreso */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">
                            Progreso: {getAnsweredCount()} de {checklist.total_questions} preguntas
                        </span>
                        <span className="fw-bold">{getProgress()}%</span>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                        <div 
                            className="progress-bar bg-primary" 
                            style={{ width: `${getProgress()}%` }}
                        />
                    </div>
                </div>

                {/* Paso: Introducción */}
                {currentStep === 'intro' && (
                    <DefaultCard>
                        <div className="text-center py-4">
                            <div 
                                className="rounded-circle bg-primary d-inline-flex align-items-center justify-content-center mb-4"
                                style={{ width: '80px', height: '80px' }}
                            >
                                <i className="fas fa-clipboard-check fa-2x text-white"></i>
                            </div>
                            <h2 className="fw-bold mb-3">{checklist.name}</h2>
                            <p className="text-muted mb-4" style={{ maxWidth: '600px', margin: '0 auto' }}>
                                {checklist.description}
                            </p>
                            
                            {checklist.instructions && (
                                <div className="alert alert-info text-start mb-4" style={{ maxWidth: '600px', margin: '0 auto' }}>
                                    <h6 className="fw-bold">
                                        <i className="fas fa-info-circle me-2"></i>
                                        Instrucciones
                                    </h6>
                                    <p className="mb-0">{checklist.instructions}</p>
                                </div>
                            )}

                            <div className="d-flex justify-content-center gap-3 mb-4">
                                <div className="text-center">
                                    <h4 className="text-primary fw-bold mb-0">{checklist.total_questions}</h4>
                                    <small className="text-muted">Preguntas</small>
                                </div>
                                <div className="text-center">
                                    <h4 className="text-success fw-bold mb-0">{checklist.max_possible_score}</h4>
                                    <small className="text-muted">Puntos máx.</small>
                                </div>
                            </div>

                            <ButtonGeneral
                                onClick={() => setCurrentStep('questions')}
                                title="Comenzar Checklist"
                                variant="primary"
                                size="medium"
                                icon="fa-play"
                                iconPosition="left"
                            />
                        </div>
                    </DefaultCard>
                )}

                {/* Paso: Preguntas */}
                {currentStep === 'questions' && (
                    <DefaultCard>
                        <div className="mb-4">
                            <small className="text-muted">
                                Pregunta {currentQuestionIndex + 1} de {checklist.total_questions}
                            </small>
                        </div>

                        <div className="mb-4">
                            <h4 className="fw-bold mb-2">
                                {checklist.questions[currentQuestionIndex].question_text}
                            </h4>
                            <small className="text-muted d-block mb-2">Si no aplica o no lo tienes, puedes dejarla sin marcar.</small>
                            {checklist.questions[currentQuestionIndex].help_text && (
                                <p className="text-muted">
                                    {checklist.questions[currentQuestionIndex].help_text}
                                </p>
                            )}
                        </div>

                        {/* Indicador de tipo de selección */}
                        <div className="mb-3">
                            <small className="text-muted">
                                <i className={`fas ${checklist.questions[currentQuestionIndex].allow_multiple ? 'fa-check-square' : 'fa-dot-circle'} me-1`}></i>
                                {checklist.questions[currentQuestionIndex].allow_multiple
                                    ? 'Puedes seleccionar varias opciones'
                                    : 'Selecciona una opción'}
                            </small>
                        </div>

                        {/* Opciones */}
                        <div className="mb-4">
                            {checklist.questions[currentQuestionIndex].options.map((option) => {
                                const questionId = checklist.questions[currentQuestionIndex].id;
                                const isMultiple = checklist.questions[currentQuestionIndex].allow_multiple;
                                const selectedIds = answers[questionId] || [];
                                const isSelected = selectedIds.includes(option.id);
                                
                                return (
                                    <div 
                                        key={option.id}
                                        className={`p-3 mb-2 border rounded ${
                                            isSelected
                                                ? 'border-primary bg-primary text-white'
                                                : 'hover-bg-light'
                                        }`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleSelectOption(questionId, option.id, isMultiple)}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className={`${isMultiple ? 'rounded' : 'rounded-circle'} border d-flex align-items-center justify-content-center me-3 flex-shrink-0 ${
                                                    isSelected ? 'border-white bg-white' : ''
                                                }`}
                                                style={{ width: '24px', height: '24px', minWidth: '24px' }}
                                            >
                                                {isSelected && (
                                                    <i className="fas fa-check text-primary" style={{ fontSize: '12px' }}></i>
                                                )}
                                            </div>
                                            <span className={isSelected ? 'text-white' : ''}>{option.option_text}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Navegación */}
                        <div className="d-flex justify-content-between">
                            <ButtonGeneral
                                onClick={handlePrevious}
                                title="Anterior"
                                variant="outline"
                                size="small"
                                icon="fa-arrow-left"
                                iconPosition="left"
                                disabled={currentQuestionIndex === 0}
                            />
                            <ButtonGeneral
                                onClick={handleNext}
                                title={currentQuestionIndex === checklist.questions.length - 1 ? 'Revisar' : 'Siguiente'}
                                variant="primary"
                                size="small"
                                icon="fa-arrow-right"
                                iconPosition="right"
                            />
                        </div>
                    </DefaultCard>
                )}

                {/* Paso: Revisión */}
                {currentStep === 'review' && (
                    <DefaultCard>
                        <h4 className="fw-bold mb-4">
                            <i className="fas fa-check-circle me-2 text-success"></i>
                            Revisa tus respuestas
                        </h4>

                        <div className="mb-4">
                            {checklist.questions.map((question, index) => {
                                const selectedIds = answers[question.id] || [];
                                const selectedOptions = question.options.filter(o => selectedIds.includes(o.id));
                                return (
                                    <div 
                                        key={question.id} 
                                        className="p-3 mb-2 border rounded d-flex justify-content-between align-items-start"
                                    >
                                        <div style={{ flex: 1 }}>
                                            <strong>Pregunta {index + 1}:</strong>
                                            <span className="ms-2">{question.question_text.length > 60 ? question.question_text.substring(0, 60) + '...' : question.question_text}</span>
                                        </div>
                                        <div className="text-end ms-3" style={{ minWidth: '150px' }}>
                                            {selectedOptions.length > 0 ? (
                                                <div className="d-flex flex-column gap-1">
                                                    {selectedOptions.map(opt => (
                                                        <span key={opt.id} className="badge bg-success">
                                                            {opt.option_text.length > 30 ? opt.option_text.substring(0, 30) + '...' : opt.option_text}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="badge bg-secondary">Sin marcar</span>
                                            )}
                                            <button
                                                className="btn btn-link btn-sm"
                                                onClick={() => {
                                                    setCurrentQuestionIndex(index);
                                                    setCurrentStep('questions');
                                                }}
                                            >
                                                Editar
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="alert alert-info small d-flex align-items-start gap-2" data-testid="checklist-ai-notice">
                            <i className="fas fa-robot mt-1" />
                            <span>
                                Al enviar, tus respuestas se analizan con inteligencia artificial (OpenAI) para generar el informe.
                                No incluyas datos personales que no sean necesarios.
                            </span>
                        </div>

                        <div className="d-flex justify-content-between">
                            <ButtonGeneral
                                onClick={() => {
                                    setCurrentQuestionIndex(checklist.questions.length - 1);
                                    setCurrentStep('questions');
                                }}
                                title="Volver a preguntas"
                                variant="outline"
                                size="small"
                                icon="fa-arrow-left"
                                iconPosition="left"
                            />
                            <ButtonGeneral
                                onClick={handleSubmit}
                                title="Enviar Respuestas"
                                variant="primary"
                                size="medium"
                                icon="fa-paper-plane"
                                iconPosition="left"
                                loading={fetchingSubmitChecklist}
                            />
                        </div>
                    </DefaultCard>
                )}
            </div>
        </div>
    );
};

export default ChecklistRespond;

